import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { InputField } from '@/components/molecules/input-field'
import { Button } from '@/components/atoms/button'
import { GuestRoomTypeType, GuestRoomTypeInputType } from '@/models/guest-room-type'
import {
  modalContainerStyle,
  modalWrapperStyle,
  modalStyle,
  modalTitleStyle,
  modalItemWrapperStyle,
  modalFooterStyle,
} from '@/components/pages/setting/guest-room/room/_room/modal/modal-layout'
import { insertAdminGuestRoomType, putAdminGuestRoomType } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { Checkbox } from '@/components/atoms/checkbox'
import { Label } from '@/components/molecules/label'

type Props = {
  title: string
  editRoom?: GuestRoomTypeType
  onClickCancel: () => void
  onClickSave: () => void
  nextDisplayOrder?: number
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
}

export const RoomTypeModal: React.FC<Props> = ({ title, onClickCancel, onClickSave, editRoom, nextDisplayOrder, setIsLoading }) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    register,
    formState: { isValid },
  } = useForm<GuestRoomTypeInputType>({
    defaultValues: {
      roomTypeId: editRoom?.roomTypeId,
      roomTypeName: editRoom?.typeNameJa,
      roomTypeShortName: editRoom?.typeShortName,
      otaCodes: editRoom?.otaCodes || [{ code: '' }],
      quantity: editRoom?.quantity,
      displayOrder: editRoom?.displayOrder || nextDisplayOrder || 1,
      displaySettings: editRoom?.displaySettings || {
        isRoomAssign: true,
        isIndicator: true,
        isCleaning: true,
      },
    },
  })
  const otaCodeInputs = useFieldArray({
    control,
    name: 'otaCodes',
  })

  const { errorHandler } = useErrorHandler()

  const save = async (formValues: GuestRoomTypeInputType) => {
    try {
      setIsLoading(true)
      if (editRoom) {
        await putAdminGuestRoomType(editRoom.id, formValues)
      } else {
        await insertAdminGuestRoomType(formValues)
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }

    onClickSave()
  }

  return (
    <div css={modalContainerStyle} style={{ height: '100%', alignItems: 'normal' }}>
      <div css={modalWrapperStyle} style={{ height: 'auto' }}>
        <div css={[modalStyle, { margin: '15px auto' }]}>
          <div css={modalTitleStyle}>{title}</div>
          <div css={[modalSectionStyle, { maxHeight: '80%' }]}>
            <div css={modalItemWrapperStyle}>
              <Controller
                name="displayOrder"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <InputField
                    value={value}
                    name={name}
                    handleChangeData={onChange}
                    label={t('Display order')}
                    placeholder="00000"
                    type="number"
                  />
                )}
              />

              <Controller
                name="roomTypeId"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, name } }) => (
                  <InputField
                    value={value}
                    name={name}
                    handleChangeData={onChange}
                    label={t('Room type ID')}
                    placeholder={t('Room type ID (within 4 digits)')}
                    type="number"
                    disabled={!!editRoom}
                    required
                  />
                )}
              />

              <Controller
                name="roomTypeName"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, name } }) => (
                  <InputField
                    value={value}
                    name={name}
                    handleChangeData={onChange}
                    label={t('Room type name')}
                    placeholder={t('Room type name')}
                    required
                  />
                )}
              />

              <Controller
                name="roomTypeShortName"
                control={control}
                rules={{ maxLength: 3 }}
                render={({ field: { onChange, value, name } }) => (
                  <InputField
                    value={value}
                    name={name}
                    handleChangeData={onChange}
                    label={t('Room type short name')}
                    placeholder={`${t('Room type short name')}(${t('maxLength', { max: 3 })})`}
                  />
                )}
              />

              <Controller
                name="quantity"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value, name } }) => (
                  <InputField
                    value={value}
                    name={name}
                    handleChangeData={onChange}
                    label={t('Number of rooms')}
                    placeholder={t('Number of rooms')}
                    required
                    type="number"
                  />
                )}
              />

              {otaCodeInputs.fields.map((otaCode, index) => (
                <>
                  <input type="hidden" {...register(`otaCodes.${index}.id`)} />
                  <Controller
                    key={otaCode.id}
                    name={`otaCodes.${index}.code`}
                    control={control}
                    render={({ field: { onChange, value, name } }) => (
                      <div css={otaCodeStyle}>
                        <InputField
                          value={value}
                          name={name}
                          handleChangeData={onChange}
                          label={t('Room type code')}
                          placeholder={t('OTA code')}
                          width={'425px'}
                        />
                        <img
                          css={firstCodeDeleteStyle}
                          src={require('@/static/images/room_delete_gold.svg')}
                          onClick={() => otaCodeInputs.remove(index)}
                        />
                      </div>
                    )}
                  />
                </>
              ))}
              <div css={addRoomTypeWrapperStyle} onClick={() => otaCodeInputs.append({ code: '' })}>
                <p>{t('Add room type code')}</p>
              </div>

              <div style={{ paddingTop: 24 }}>
                <Label label={t('Display settings')} />
                <div style={{ display: 'flex' }}>
                  <Controller
                    name="displaySettings.isRoomAssign"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox onChange={onChange} value={value} label={t('Assignment')} style={checkBoxOverrideStyle} />
                    )}
                  />
                  <Controller
                    name="displaySettings.isIndicator"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox onChange={onChange} value={value} label={t('Indicator')} style={checkBoxOverrideStyle} />
                    )}
                  />
                  <Controller
                    name="displaySettings.isCleaning"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox onChange={onChange} value={value} label={t('Cleaning status')} style={checkBoxOverrideStyle} />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
          <div css={modalFooterStyle}>
            <Button
              buttonType={3}
              width={110}
              height={38}
              fontSize={14}
              marginRight={16}
              onClick={() => {
                onClickCancel()
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              buttonType={!isValid ? 5 : 1}
              width={110}
              height={38}
              fontSize={14}
              marginRight={32}
              onClick={handleSubmit(save)}
              isDisabled={!isValid}
            >
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

const modalSectionStyle = css({
  background: '#F2F2F2',
  padding: '24px 28px',
  height: 569,
  overflowY: 'scroll',
})

const addRoomTypeWrapperStyle = css({
  cursor: 'pointer',
  display: 'flex',
  height: 36,
  backgroundColor: '#FAFAFA',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px dashed #CCCCCC',
  p: {
    lineHeight: '36px',
    color: '#FF5500',
    fontSize: 12,
    fontWeight: 'bold',
    paddingLeft: 8,
  },
})

const otaCodeStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
})

const firstCodeDeleteStyle = css({
  width: 30,
  height: 30,
  marginLeft: 16,
  marginTop: 23,
})

const checkBoxOverrideStyle = css({
  minWidth: '33%',
  img: { width: 20, height: 20, p: { fontSize: 14 } },
})
